import './Firm.css';

/**
 * Reusable confirmation dialog.
 *
 * Props:
 *   open            – whether to show the dialog
 *   onClose         – called when clicking overlay or Cancel (ignored while loading)
 *   onConfirm       – called when clicking the confirm button
 *   title           – dialog heading
 *   icon            – emoji/icon character ('⚠', 'ℹ️', etc.)
 *   iconVariant     – 'warning' | 'info' (default 'info') — controls icon wrapper class
 *   confirmLabel    – text for the confirm button
 *   confirmVariant  – 'danger' | 'clear' | 'save' (default 'save') — controls button style
 *   loading         – if true, show spinner + loadingText instead of content
 *   loadingText     – primary text shown while loading
 *   loadingSubtext  – secondary text shown while loading
 *   error           – error string to display above the actions
 *   disabled        – additionally disable the confirm button
 *   children        – dialog body content
 */
const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  icon = 'ℹ️',
  iconVariant = 'info',
  confirmLabel = 'Confirm',
  confirmVariant = 'save',
  loading = false,
  loadingText = 'Processing…',
  loadingSubtext = 'This may take a moment.',
  error = null,
  disabled = false,
  children,
}) => {
  if (!open) return null;

  const handleOverlayClick = () => {
    if (!loading) onClose();
  };

  const spinnerClass = confirmVariant === 'save' ? 'firm-spinner-blue' : 'firm-spinner';

  const confirmBtnClass =
    confirmVariant === 'danger'
      ? 'firm-btn-confirm-delete'
      : confirmVariant === 'clear'
        ? 'firm-btn-confirm-clear'
        : 'firm-btn-confirm-save';

  return (
    <div className="firm-overlay" onClick={handleOverlayClick}>
      <div className="firm-dialog" onClick={(e) => e.stopPropagation()}>
        {loading ? (
          <div className="firm-saving-container">
            <div className={spinnerClass} />
            <p className="firm-saving-text">{loadingText}</p>
            {loadingSubtext && <p className="firm-saving-subtext">{loadingSubtext}</p>}
          </div>
        ) : (
          <>
            <div className={iconVariant === 'warning' ? 'firm-dialog-icon' : 'firm-dialog-icon-info'}>
              {icon}
            </div>
            <h3 className="firm-dialog-title">{title}</h3>
            {children}
            {error && <p className="firm-dialog-error">{error}</p>}
            <div className="firm-dialog-actions">
              <button className="firm-btn firm-btn-secondary" onClick={onClose}>Cancel</button>
              <button className={confirmBtnClass} disabled={disabled} onClick={onConfirm}>
                {confirmLabel}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ConfirmDialog;